import { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';
import {
  Box,
  Button,
  CircularProgress,
  Column,
  Flex,
  Grid,
  IconButton,
  Row,
  Screen,
} from '@tempmee/react';
import { OutlinePencilIcon, OutlineTrashIcon, OutlineXIcon } from '@tempmee/react-icons';
import history from '~/browserHistory';
import { BackButton } from '~/commonComponents';
import { useCancelJobMutation, useGetJob } from '~/domain/Jobs';
import { IJob } from '~/models';
import { routes } from '~/routes';
import AnimatedCloseCircle from '../../shared/AnimatedCloseCircle';
import ConfirmPopup from '../../shared/ConfirmPopup';
import { InterviewInviteSection } from './InterviewInviteSection';
import { InviteSection } from './InviteSection';
import { ShiftDetailsInfo } from './ShiftDetailsInfo';

/**
 * Where a job this screen does not show belongs: the confirm screen while a
 * candidate is waiting, the summary once the shift is filled or worked. `null` for
 * a job that stays here.
 */
const getStatusRedirectPath = (job: IJob | null): string | null => {
  if (!job) return null;

  if (job.status === 'pending' || job.status === 'counter') {
    const candidateId = job.multipleCounters?.[0]?.candidate?.id;
    return generatePath(routes.confirmCandidate, {
      jobId: job.id,
      candidateId: candidateId ?? '',
    });
  }

  if (job.status === 'filled' || job.status === 'pay-out' || job.status === 'complete') {
    return generatePath(routes.jobDetails, { jobId: job.id });
  }

  return null;
};

/**
 * Redesigned open Shift/Interview Details page. Two-column layout: the inline
 * invite/candidate list on the left, the read-only details on the right. Temp
 * open shifts show the professional invite list; perm onsite interviews show the
 * eligible-candidate list and hide Auto-Confirm. Gated by the
 * `ShiftDetailsInviteSection` experiment; the `control` variant renders the
 * legacy single-column screen.
 */
export default function OpenJobScreenRedesign() {
  const params = useParams<{ jobId?: string }>();

  const locationState = history.location.state as
    | { jobId?: string; isJobPost?: boolean }
    | undefined;
  const jobId = locationState ? locationState.jobId : params?.jobId;

  const { data: job = null, isLoading: isJobLoading, isFetching } = useGetJob(jobId);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();
  const isLoading = isJobLoading || isCancelPending;

  const [cancelJobPopup, setCancelJobPopup] = useState(false);

  const statusRedirectPath = getStatusRedirectPath(job);

  // React Query serves the cached job while the invalidated refetch is in flight — the
  // confirm screen's decline sends the office back here while that copy still reads
  // 'counter' — so route on the settled job rather than that copy (GOTU-19091). A
  // failed refetch leaves the copy in place, and the screen routes on it as before.
  useEffect(() => {
    if (!statusRedirectPath || isFetching) return;
    history.replace(statusRedirectPath);
  }, [statusRedirectPath, isFetching]);

  // A job this screen is leaving shows the spinner, not its details, while the
  // refetch decides where it goes — an open job never waits here.
  if (isLoading || !job || statusRedirectPath) {
    return (
      <Screen variant="standard">
        <Screen.Body>
          <Flex justify="center" py={10}>
            <CircularProgress />
          </Flex>
        </Screen.Body>
      </Screen>
    );
  }

  const isInterview = job.isOnsiteInterview;

  // Edit routes to the same job-edit screen for both shifts and interviews,
  // matching the legacy flow (OpenJobScreen).
  const editPath = generatePath(routes.editOpenJob, { jobId: job.id });

  return (
    <>
      <Screen variant="standard">
        <Screen.Header
          start={
            <Box className="-ml-3">
              {locationState?.isJobPost ? (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  startIcon={<OutlineXIcon />}
                  data-cy="shift-details-close"
                  onClick={() => history.push('/dashboard/home')}
                >
                  Close
                </Button>
              ) : (
                <BackButton />
              )}
            </Box>
          }
          title={isInterview ? 'Interview Details' : 'Shift Details'}
          end={
            <Row align="center" gap={10}>
              <IconButton
                type="button"
                color="primary"
                appearance="filled"
                size="medium"
                data-cy="edit-shift-button"
                aria-label="Edit"
                onClick={() => history.push(editPath)}
                Icon={<OutlinePencilIcon />}
              >
                Edit
              </IconButton>
              <IconButton
                type="button"
                color="primary"
                appearance="outline"
                size="medium"
                data-cy="cancel-shift-button"
                aria-label="Cancel"
                onClick={() => setCancelJobPopup(true)}
                Icon={<OutlineTrashIcon />}
              >
                Cancel
              </IconButton>
            </Row>
          }
        />
        <Screen.Body>
          <Flex justify="center" pt={16} pb={16}>
            <Box width="min(1200px, 100%)">
              <Grid columns="4fr 3fr" gap={16} align="start">
                {isInterview ? <InterviewInviteSection job={job} /> : <InviteSection job={job} />}
                <Column gap={10}>
                  <Box pt={isInterview ? 0 : 10}>
                    <ShiftDetailsInfo job={job} />
                  </Box>
                </Column>
              </Grid>
            </Box>
          </Flex>
        </Screen.Body>
      </Screen>

      {cancelJobPopup && (
        <ConfirmPopup
          Icon={() => <AnimatedCloseCircle />}
          title="Are you sure you want to cancel?"
          description={
            isInterview
              ? 'Cancelling an onsite interview means it will no longer be available to the candidates you invited.'
              : 'Cancelling an open shift means that it will no longer be available to professionals around your office to accept and potentially work.'
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setCancelJobPopup(false)}
          leftButtonDataCy="cancel-popup-go-back"
          rightButtonText={isInterview ? 'Cancel Interview' : 'Cancel Shift'}
          rightButtonDataCy="confirm-cancel-shift"
          rightButtonAction={() => {
            cancelJob({ jobId: job.id, redirect: true });
            setCancelJobPopup(false);
          }}
        />
      )}
    </>
  );
}
